/** Buyer H5 保真度补齐 — 专用 mock（T1）
 * 数据全部收口于此，页面仅 import 消费，不在前端硬编码业务字段。
 */

import type {
  BuyerOrderPayment,
  BuyerClaim,
  DocumentReport,
  PaymentPlanItem,
  BluCreditProfile,
  TemperatureSeries,
  ClaimSummaryStats,
  BuyerOrderDetail,
  BuyerProfile,
  Container,
  NodeRecord,
} from '../types';
import { OrderDisplayStatus, WaterSlipStatus, PaymentNode, ContainerType, ContainerStatus, NodeStatus } from '../types';
import { DEFAULT_LIABILITY_SEGMENTS, TEMP_THRESHOLD } from '../utils';
import { NODE_NAMES } from '../utils/constants';

// ===== BluCredit 完整档案（Q6 扩展，A/$2M/$1.02M/2026-08-20/可用$980K） =====
export const mockBluCredit: BluCreditProfile = {
  available: 980000,
  rating: 'A',
  totalLimit: 2000000,
  usedAmount: 1020000,
  nextDueDate: '2026-08-20',
};

// ===== 收款计划时间轴构造（30% 已核销带水单号 / 70% 待付带剩余天数） =====
function buildReceivablePlan(amount: number, slipNo: string, remainingDays: number): PaymentPlanItem[] {
  return [
    {
      node: PaymentNode.T_T_DEPOSIT,
      label: '定金 (30%)',
      ratio: 0.3,
      amount: Math.round(amount * 0.3),
      status: WaterSlipStatus.VERIFIED,
      waterSlipNo: slipNo,
    },
    {
      node: PaymentNode.T_T_BALANCE,
      label: '尾款 (70%)',
      ratio: 0.7,
      amount: Math.round(amount * 0.7),
      status: WaterSlipStatus.UNSUBMITTED,
      remainingDays,
    },
  ];
}

const ROUTE = '青岛 → 迪拜';
const SPEC = '5.5cm+·28T·10kg网袋';

// ===== 买家订单（在途 8 单 / 含 1 单温度报警 / 含 BluCredit 档案） =====
// 在途金额合计 = 1,020,000（头图 $1.02M）
export const mockBuyerOrders: BuyerOrderPayment[] = [
  {
    id: '1', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    productName: '大蒜 · 新鲜农产品', amount: 180000, currency: 'USD', dueDate: '2025-08-30',
    status: 'PARTIALLY_PAID', paidAmount: 54000, remainingAmount: 126000,
    bluCreditAvailable: true, bluCreditAmount: 50000,
    displayStatus: OrderDisplayStatus.TEMPERATURE_ALERT,
    isHistory: false, containerNo: 'MSCU-7723456', route: ROUTE, spec: SPEC,
    bluCredit: mockBluCredit,
    receivablePlan: buildReceivablePlan(180000, 'WS-2025-0001-A3F8', 12),
  },
  {
    id: '2', orderId: 'BP-2025-0002', orderNo: 'BP-2025-0002',
    productName: '大姜 · 新鲜农产品', amount: 150000, currency: 'USD', dueDate: '2025-08-25',
    status: 'PARTIALLY_PAID', paidAmount: 45000, remainingAmount: 105000,
    bluCreditAvailable: true, bluCreditAmount: 50000,
    displayStatus: OrderDisplayStatus.NORMAL,
    isHistory: false, containerNo: 'MSCU-7723457', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(150000, 'WS-2025-0002-B7C2', 9),
  },
  {
    id: '3', orderId: 'BP-2025-0003', orderNo: 'BP-2025-0003',
    productName: '苹果 · 新鲜水果', amount: 140000, currency: 'USD', dueDate: '2025-09-05',
    status: 'PAID', paidAmount: 140000, remainingAmount: 0,
    bluCreditAvailable: false, bluCreditAmount: 0,
    displayStatus: OrderDisplayStatus.NORMAL,
    isHistory: false, containerNo: 'MSCU-7723458', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(140000, 'WS-2025-0003-C9D1', 15),
  },
  {
    id: '4', orderId: 'BP-2025-0004', orderNo: 'BP-2025-0004',
    productName: '梨 · 新鲜水果', amount: 130000, currency: 'USD', dueDate: '2025-09-10',
    status: 'PARTIALLY_PAID', paidAmount: 39000, remainingAmount: 91000,
    bluCreditAvailable: true, bluCreditAmount: 50000,
    displayStatus: OrderDisplayStatus.NORMAL,
    isHistory: false, containerNo: 'MSCU-7723459', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(130000, 'WS-2025-0004-D1E5', 18),
  },
  {
    id: '5', orderId: 'BP-2025-0005', orderNo: 'BP-2025-0005',
    productName: '橙子 · 新鲜水果', amount: 120000, currency: 'USD', dueDate: '2025-09-12',
    status: 'UNPAID', paidAmount: 0, remainingAmount: 120000,
    bluCreditAvailable: true, bluCreditAmount: 50000,
    displayStatus: OrderDisplayStatus.NORMAL,
    isHistory: false, containerNo: 'MSCU-7723460', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(120000, 'WS-2025-0005-E2F6', 20),
  },
  {
    id: '6', orderId: 'BP-2025-0006', orderNo: 'BP-2025-0006',
    productName: '葡萄 · 新鲜水果', amount: 110000, currency: 'USD', dueDate: '2025-09-15',
    status: 'UNPAID', paidAmount: 0, remainingAmount: 110000,
    bluCreditAvailable: true, bluCreditAmount: 50000,
    displayStatus: OrderDisplayStatus.DOCUMENTS_PENDING,
    isHistory: false, containerNo: 'MSCU-7723461', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(110000, 'WS-2025-0006-F3A7', 22),
  },
  {
    id: '7', orderId: 'BP-2025-0007', orderNo: 'BP-2025-0007',
    productName: '柠檬 · 新鲜水果', amount: 100000, currency: 'USD', dueDate: '2025-09-18',
    status: 'PARTIALLY_PAID', paidAmount: 30000, remainingAmount: 70000,
    bluCreditAvailable: false, bluCreditAmount: 0,
    displayStatus: OrderDisplayStatus.NORMAL,
    isHistory: false, containerNo: 'MSCU-7723462', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(100000, 'WS-2025-0007-A8B9', 25),
  },
  {
    id: '8', orderId: 'BP-2025-0008', orderNo: 'BP-2025-0008',
    productName: '芒果 · 新鲜水果', amount: 90000, currency: 'USD', dueDate: '2025-09-20',
    status: 'UNPAID', paidAmount: 0, remainingAmount: 90000,
    bluCreditAvailable: true, bluCreditAmount: 50000,
    displayStatus: OrderDisplayStatus.NORMAL,
    isHistory: false, containerNo: 'MSCU-7723463', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(90000, 'WS-2025-0008-C4D2', 28),
  },
  {
    id: '9', orderId: 'BP-2024-0099', orderNo: 'BP-2024-0099',
    productName: '核桃 · 坚果', amount: 220000, currency: 'USD', dueDate: '2024-12-20',
    status: 'PAID', paidAmount: 220000, remainingAmount: 0,
    bluCreditAvailable: false, bluCreditAmount: 0,
    displayStatus: OrderDisplayStatus.COMPLETED,
    isHistory: true, containerNo: 'MSCU-6612001', route: ROUTE, spec: SPEC,
    receivablePlan: buildReceivablePlan(220000, 'WS-2024-0099-Z1Y2', 0),
  },
];

// ===== 理赔（活动 1 + 历史 3） =====
export const mockBuyerClaims: BuyerClaim[] = [
  {
    id: 'c1', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    claimType: 'temperature_exceeded',
    amount: 8512, currency: 'USD', status: '处理中',
    description: '大蒜货物温度超限 8.2°C 持续 2h15min，触发 BluGuard 预案',
    submittedAt: '2025-07-15 14:20', resolvedAt: '', evidence: [],
    claimNo: 'CLM-2607-0047', workOrderStatus: 'assessing',
    steps: [
      { key: 'submitted', label: '已提交', status: 'done' },
      { key: 'accepted', label: '已受理', status: 'done' },
      { key: 'assessing', label: '定损中', status: 'current' },
      { key: 'pending_payout', label: '待赔付', status: 'todo' },
    ],
    payableAmount: 8512, cifAmount: 42560, payoutRatio: 20,
    isHistory: false,
  },
  {
    id: 'c2', orderId: 'BP-2025-0002', orderNo: 'BP-2025-0002',
    claimType: 'cargo_damage',
    amount: 3200, currency: 'USD', status: '已结案',
    description: '到港验收发现外包装破损 3 箱，定责海运段',
    submittedAt: '2025-07-10 09:30', resolvedAt: '2025-07-22 10:00', evidence: [],
    claimNo: 'CLM-2607-0039', workOrderStatus: 'settled_paid',
    steps: [
      { key: 'submitted', label: '已提交', status: 'done' },
      { key: 'accepted', label: '已受理', status: 'done' },
      { key: 'assessing', label: '定损中', status: 'done' },
      { key: 'pending_payout', label: '待赔付', status: 'done' },
    ],
    payableAmount: 3200, cifAmount: 40000, payoutRatio: 8,
    liabilitySegments: DEFAULT_LIABILITY_SEGMENTS,
    isHistory: true,
  },
  {
    id: 'c3', orderId: 'BP-2025-0003', orderNo: 'BP-2025-0003',
    claimType: 'shortage',
    amount: 0, currency: 'USD', status: '已结案',
    description: '短装异议，证据不足拒赔',
    submittedAt: '2025-07-05 16:00', resolvedAt: '2025-07-18 14:00', evidence: [],
    claimNo: 'CLM-2607-0035', workOrderStatus: 'settled_rejected',
    steps: [
      { key: 'submitted', label: '已提交', status: 'done' },
      { key: 'accepted', label: '已受理', status: 'done' },
      { key: 'assessing', label: '定损中', status: 'done' },
      { key: 'pending_payout', label: '待赔付', status: 'todo' },
    ],
    payableAmount: 0, cifAmount: 38000, payoutRatio: 0,
    liabilitySegments: DEFAULT_LIABILITY_SEGMENTS,
    isHistory: true,
  },
  {
    id: 'c4', orderId: 'BP-2025-0004', orderNo: 'BP-2025-0004',
    claimType: 'iot_offline',
    amount: 1500, currency: 'USD', status: '已结案',
    description: 'IoT 离线期间温度缺失，按比例赔付',
    submittedAt: '2025-06-28 11:00', resolvedAt: '2025-07-09 09:00', evidence: [],
    claimNo: 'CLM-2606-0021', workOrderStatus: 'settled_paid',
    steps: [
      { key: 'submitted', label: '已提交', status: 'done' },
      { key: 'accepted', label: '已受理', status: 'done' },
      { key: 'assessing', label: '定损中', status: 'done' },
      { key: 'pending_payout', label: '待赔付', status: 'done' },
    ],
    payableAmount: 1500, cifAmount: 30000, payoutRatio: 5,
    liabilitySegments: DEFAULT_LIABILITY_SEGMENTS,
    isHistory: true,
  },
];

// ===== 理赔头图统计（处理中 1 / 已结案 3 / 已获赔 $15.6K） =====
export const mockBuyerClaimStats: ClaimSummaryStats = {
  processing: 1,
  closed: 3,
  paidAmount: 15600,
};

// ===== 单证与报告（四证包4 / 到港1 / 温控1 / 其他2） =====
export const mockBuyerDocuments: DocumentReport[] = [
  {
    id: 'd1', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'CI', documentName: '商业发票 Commercial Invoice', fileUrl: '',
    issuedAt: '2025-07-01', status: 'verified', language: '中英双语',
    category: 'cert_pack', fileSizeKb: 128,
    hash: 'a3f8b2c9d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f1e7',
    chainVerified: true,
  },
  {
    id: 'd2', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'PL', documentName: '装箱单 Packing List', fileUrl: '',
    issuedAt: '2025-07-01', status: 'verified', language: '中英双语',
    category: 'cert_pack', fileSizeKb: 96,
    hash: 'b7c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2',
    chainVerified: true,
  },
  {
    id: 'd3', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'BL', documentName: '提单 Bill of Lading', fileUrl: '',
    issuedAt: '2025-07-03', status: 'verified', language: '英文',
    category: 'cert_pack', fileSizeKb: 210,
    hash: 'c9d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1',
    chainVerified: true,
  },
  {
    id: 'd4', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'Phyto', documentName: '植物检疫证书 Phytosanitary', fileUrl: '',
    issuedAt: '2025-07-02', status: 'verified', language: '中英双语',
    category: 'cert_pack', fileSizeKb: 154,
    hash: 'd1e5f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1',
    chainVerified: true,
  },
  {
    id: 'd5', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'Report', documentName: '到港验收报告 Arrival Report', fileUrl: '',
    issuedAt: '2025-07-20', status: 'pending', language: '中文',
    category: 'arrival', fileSizeKb: 320,
    hash: 'e2f6a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2',
    chainVerified: true,
  },
  {
    id: 'd6', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'Report', documentName: '温控达标报告 Temperature Compliance Report', fileUrl: '',
    issuedAt: '2025-07-21', status: 'verified', language: '中文',
    category: 'temp_control', fileSizeKb: 188,
    hash: 'f3a7b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3',
    chainVerified: true, tempCompliant: true, reportKind: 'temp_control',
  },
  {
    id: 'd7', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'Report', documentName: '碳足迹证书 Carbon Footprint Certificate', fileUrl: '',
    issuedAt: '2025-07-22', status: 'verified', language: '中英双语',
    category: 'other', fileSizeKb: 142,
    hash: 'a8b9c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3',
    chainVerified: true, carbonReduction: 0.8, reportKind: 'carbon',
  },
  {
    id: 'd8', orderId: 'BP-2025-0001', orderNo: 'BP-2025-0001',
    documentType: 'Report', documentName: '质量合格证 Quality Certificate', fileUrl: '',
    issuedAt: '2025-07-02', status: 'verified', language: '中文',
    category: 'other', fileSizeKb: 110,
    hash: 'c4d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2',
    chainVerified: true,
  },
];

// ===== 温度序列（含 8.2°C 超限点，15min 间隔，超限 9 点 = 2h15m） =====
export const mockTemperatureSeries: TemperatureSeries[] = [
  { time: '00:00', temp: -3.0 },
  { time: '01:00', temp: -2.9 },
  { time: '02:00', temp: -3.1 },
  { time: '03:00', temp: -2.8 },
  { time: '04:00', temp: -3.0 },
  { time: '04:15', temp: 8.2 },
  { time: '04:30', temp: 7.8 },
  { time: '04:45', temp: 8.0 },
  { time: '05:00', temp: 7.5 },
  { time: '05:15', temp: 6.9 },
  { time: '05:30', temp: 5.2 },
  { time: '05:45', temp: 4.5 },
  { time: '06:00', temp: 3.8 },
  { time: '07:00', temp: -2.9 },
  { time: '08:00', temp: -3.0 },
];

/** 工具：温度是否超限（超 TEMP_THRESHOLD.UPPER） */
export function hasTemperatureOver(series: TemperatureSeries[]): boolean {
  return series.some((p) => p.temp > TEMP_THRESHOLD.UPPER);
}

// =====================================================================
// 买家侧 IA 调整 — 订单-柜关联对齐 mock（隔离 admin 共享 mockContainers）
// 对齐 mockBuyerOrders 的 orderId / containerNo，打通 drill-down。
// =====================================================================

/** 用 NODE_NAMES 构造 10 节点历史（alertNode 指定则置 ALERT 并前置节点全 COMPLETED） */
function buildNodeHistory(currentNode: number, alertNode: number | null): NodeRecord[] {
  return NODE_NAMES.map((name, idx) => {
    const nodeId = idx + 1;
    let status: NodeStatus;
    if (alertNode && nodeId < alertNode) status = NodeStatus.COMPLETED;
    else if (alertNode && nodeId === alertNode) status = NodeStatus.ALERT;
    else if (nodeId < currentNode) status = NodeStatus.COMPLETED;
    else if (nodeId === currentNode) status = NodeStatus.IN_PROGRESS;
    else status = NodeStatus.PENDING;
    return {
      nodeId,
      nodeName: name,
      status,
      operatorId: `OP${String((idx % 10) + 1).padStart(3, '0')}`,
      operatorName: status !== NodeStatus.PENDING ? '系统操作员' : '',
      timestamp: status !== NodeStatus.PENDING ? `2025-07-${String((idx % 27) + 1).padStart(2, '0')} 10:00` : '',
      photos: status === NodeStatus.COMPLETED ? [`/photos/order_node_${nodeId}.jpg`] : [],
      notes: status === NodeStatus.COMPLETED ? '检验合格，无异常' : status === NodeStatus.ALERT ? '温度超限告警' : '',
    };
  });
}

const BUYER_LOCATION = { lat: 25.2048, lng: 55.2708, portName: '迪拜港', country: '阿联酋' };

/** 买家对齐柜（每单 1 柜；BP-2025-0001 为温度报警单，alertNode=6 即海运节点） */
export const mockBuyerContainers: Container[] = [
  {
    id: 'CONT-BP-0001', containerNo: 'MSCU-7723456', orderId: 'BP-2025-0001',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90001',
    currentNode: 6, totalNodes: 10,
    nodeHistory: buildNodeHistory(6, 6),
    iotDeviceId: 'DEV-90001', temperature: 8.2, humidity: 72,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'MSC DIANA', voyageNo: 'FD841W',
    estimatedArrival: '2025-08-12T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0001', category: '新鲜农产品', route: '青岛 → 迪拜', remainingStay: '2天3小时',
  },
  {
    id: 'CONT-BP-0002', containerNo: 'MSCU-7723457', orderId: 'BP-2025-0002',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90002',
    currentNode: 5, totalNodes: 10,
    nodeHistory: buildNodeHistory(9, null),
    iotDeviceId: 'DEV-90002', temperature: -3.0, humidity: 70,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'COSCO SHIPPING LEO', voyageNo: 'V087E',
    estimatedArrival: '2025-08-08T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0002', category: '新鲜农产品', route: '青岛 → 迪拜', remainingStay: '1天5小时',
  },
  {
    id: 'CONT-BP-0003', containerNo: 'MSCU-7723458', orderId: 'BP-2025-0003',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90003',
    currentNode: 8, totalNodes: 10,
    nodeHistory: buildNodeHistory(13, null),
    iotDeviceId: 'DEV-90003', temperature: -2.9, humidity: 68,
    location: BUYER_LOCATION, status: ContainerStatus.AT_CUSTOMS,
    vesselName: 'MAERSK MCKINNEY', voyageNo: 'M2512',
    estimatedArrival: '2025-08-03T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0003', category: '新鲜水果', route: '青岛 → 迪拜', remainingStay: '3天1小时',
  },
  {
    id: 'CONT-BP-0004', containerNo: 'MSCU-7723459', orderId: 'BP-2025-0004',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90004',
    currentNode: 4, totalNodes: 10,
    nodeHistory: buildNodeHistory(7, null),
    iotDeviceId: 'DEV-90004', temperature: -3.1, humidity: 71,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'CMA CGM JACQUES', voyageNo: '0PG5WE',
    estimatedArrival: '2025-08-15T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0004', category: '新鲜水果', route: '青岛 → 迪拜', remainingStay: '1天2小时',
  },
  {
    id: 'CONT-BP-0005', containerNo: 'MSCU-7723460', orderId: 'BP-2025-0005',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90005',
    currentNode: 4, totalNodes: 10,
    nodeHistory: buildNodeHistory(6, null),
    iotDeviceId: 'DEV-90005', temperature: -3.0, humidity: 69,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'ONE APUS', voyageNo: '073E',
    estimatedArrival: '2025-08-18T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0005', category: '新鲜水果', route: '青岛 → 迪拜', remainingStay: '1天0小时',
  },
  {
    id: 'CONT-BP-0006', containerNo: 'MSCU-7723461', orderId: 'BP-2025-0006',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90006',
    currentNode: 4, totalNodes: 10,
    nodeHistory: buildNodeHistory(5, null),
    iotDeviceId: 'DEV-90006', temperature: -2.8, humidity: 67,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'EVER GOLDEN', voyageNo: 'EG135N',
    estimatedArrival: '2025-08-20T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0006', category: '新鲜水果', route: '青岛 → 迪拜', remainingStay: '2天0小时',
  },
  {
    id: 'CONT-BP-0007', containerNo: 'MSCU-7723462', orderId: 'BP-2025-0007',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90007',
    currentNode: 5, totalNodes: 10,
    nodeHistory: buildNodeHistory(8, null),
    iotDeviceId: 'DEV-90007', temperature: -3.2, humidity: 66,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'HMM ALGECIRAS', voyageNo: 'H045E',
    estimatedArrival: '2025-08-22T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0007', category: '新鲜水果', route: '青岛 → 迪拜', remainingStay: '1天3小时',
  },
  {
    id: 'CONT-BP-0008', containerNo: 'MSCU-7723463', orderId: 'BP-2025-0008',
    type: ContainerType.REEFER_40, sealNo: 'SEAL-90008',
    currentNode: 2, totalNodes: 10,
    nodeHistory: buildNodeHistory(4, null),
    iotDeviceId: 'DEV-90008', temperature: -3.0, humidity: 70,
    location: BUYER_LOCATION, status: ContainerStatus.IN_TRANSIT,
    vesselName: 'YANG MING WISH', voyageNo: 'YM109S',
    estimatedArrival: '2025-08-25T00:00:00.000Z', actualArrival: '',
    archiveNo: 'DA-2025-0008', category: '新鲜水果', route: '青岛 → 迪拜', remainingStay: '1天1小时',
  },
];

/** 温度序列：报警单含 8.2°C 超限点（与 mockTemperatureSeries 一致） */
const ALERT_TEMP_SERIES: TemperatureSeries[] = [
  { time: '00:00', temp: -3.0 },
  { time: '01:00', temp: -2.9 },
  { time: '02:00', temp: -3.1 },
  { time: '03:00', temp: -2.8 },
  { time: '04:00', temp: -3.0 },
  { time: '04:15', temp: 8.2 },
  { time: '04:30', temp: 7.8 },
  { time: '04:45', temp: 8.0 },
  { time: '05:00', temp: 7.5 },
  { time: '05:15', temp: 6.9 },
  { time: '05:30', temp: 5.2 },
  { time: '05:45', temp: 4.5 },
  { time: '06:00', temp: 3.8 },
  { time: '07:00', temp: -2.9 },
  { time: '08:00', temp: -3.0 },
];

/** 温度序列：常态 -3°C */
const NORMAL_TEMP_SERIES: TemperatureSeries[] = [
  { time: '00:00', temp: -3.0 },
  { time: '01:00', temp: -2.9 },
  { time: '02:00', temp: -3.1 },
  { time: '03:00', temp: -2.8 },
  { time: '04:00', temp: -3.0 },
  { time: '05:00', temp: -3.0 },
  { time: '06:00', temp: -2.9 },
  { time: '07:00', temp: -3.1 },
  { time: '08:00', temp: -3.0 },
  { time: '09:00', temp: -2.8 },
  { time: '10:00', temp: -3.0 },
  { time: '11:00', temp: -3.0 },
  { time: '12:00', temp: -2.9 },
  { time: '13:00', temp: -3.1 },
  { time: '14:00', temp: -3.0 },
];

/** 按订单返回温度序列：报警单返回超限序列，其余返回常态 -3°C 序列 */
export function mockBuyerTemperatureSeries(orderId: string): TemperatureSeries[] {
  return orderId === 'BP-2025-0001' ? ALERT_TEMP_SERIES : NORMAL_TEMP_SERIES;
}

/** 「我的」页账户概览（不含 BluCredit） */
export const mockBuyerProfile: BuyerProfile = {
  name: 'FreshMart UAE',
  company: 'FreshMart Trading LLC',
  country: '阿联酋',
  creditRating: 'A',
  bluGuardLevel: 'BluGuard 标准版',
  joinedAt: '2023-06-15',
};

// ===== byOrder 取数函数（详情页 drilldown 过滤） =====

/** 按 orderId（兼容 id）取订单主档 */
export function getOrderById(orderId: string): BuyerOrderPayment | undefined {
  return mockBuyerOrders.find((o) => o.orderId === orderId || o.id === orderId);
}

/** 按 orderId 取该订单柜列表（预留多柜，本期 1 柜） */
export function getContainersByOrder(orderId: string): Container[] {
  return mockBuyerContainers.filter((c) => c.orderId === orderId);
}

/** 按 orderId 过滤单证报告 */
export function getDocumentsByOrder(orderId: string): DocumentReport[] {
  return mockBuyerDocuments.filter((d) => d.orderId === orderId);
}

/** 按 orderId 过滤理赔 */
export function getClaimsByOrder(orderId: string): BuyerClaim[] {
  return mockBuyerClaims.filter((c) => c.orderId === orderId);
}

/** 按 orderId 取温度序列 */
export function getTemperatureSeriesByOrder(orderId: string): TemperatureSeries[] {
  return mockBuyerTemperatureSeries(orderId);
}

/** 聚合：订单详情一次性取数 */
export function getOrderDetail(orderId: string): BuyerOrderDetail | null {
  const order = getOrderById(orderId);
  if (!order) return null;
  return {
    order,
    containers: getContainersByOrder(orderId),
    documents: getDocumentsByOrder(orderId),
    claims: getClaimsByOrder(orderId),
  };
}
